/**
 * Enumé — API base URL resolver
 * =============================
 * THE SINGLE SOURCE OF TRUTH FOR "WHERE IS THE BACKEND".
 *
 * Replaces thirteen separate declarations across seven files that used three
 * mutually incompatible fallbacks ('' / '/api' / 'http://localhost:5000').
 *
 * ---------------------------------------------------------------------------
 * WHY TWO EXPORTS
 * ---------------------------------------------------------------------------
 * The codebase genuinely needs both, and pretending otherwise is what produced
 * the `.replace(/\/api\/?$/, '')` hacks in RealtimeContext.jsx and
 * ShareableLineageStory.jsx.
 *
 *   API_BASE    → REST calls.        `${API_BASE}/projects/5`
 *   API_ORIGIN  → everything already carrying its own /api prefix, and SSE.
 *
 * The media case is the subtle one. Uploaded files are stored in the DB WITH
 * the /api prefix baked in — verified 2026-08-16 against redsocialdb2026:
 *
 *   digital_items.file_url  →  /api/projects/uploads/<file>.jpg   (6 rows)
 *
 * and VITE_API_URL in the SWA workflow ALSO ends in /api. So concatenating a
 * stored media path onto API_BASE yields  https://host/api/api/uploads/...
 * — a 404. Stored media paths must be joined to API_ORIGIN.
 *
 *   CORRECT:  `${API_ORIGIN}${project.image_url}`
 *   WRONG:    `${API_BASE}${project.image_url}`
 *
 * This is currently masked in production because models.py runs every URL
 * through _resolve(), which rewrites relative paths to absolute Azure Blob
 * URLs, so the frontend's `.startsWith('http')` guards short-circuit. Do not
 * rely on that: it only holds while blob storage is configured and every row
 * has been migrated.
 *
 * ---------------------------------------------------------------------------
 * NORMALIZATION
 * ---------------------------------------------------------------------------
 * Accepts any of these and produces the same result:
 *
 *   https://host.net/api      →  ORIGIN https://host.net   BASE https://host.net/api
 *   https://host.net/api/     →  ORIGIN https://host.net   BASE https://host.net/api
 *   https://host.net          →  ORIGIN https://host.net   BASE https://host.net/api
 *   (unset, dev only)         →  ORIGIN ''                 BASE '/api'
 *
 * The unset case yields relative URLs, which the Vite dev proxy forwards to
 * localhost:5000. That is why this file must NOT throw in development — see
 * below.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS NO LONGER THROWS IN DEV
 * ---------------------------------------------------------------------------
 * The previous version threw whenever VITE_API_URL was unset, with no mode
 * check. That is right for production and wrong for local dev, where unset is
 * the normal, working configuration. Since this module is imported at the top
 * of a component file, throwing here happens at module evaluation — before
 * React mounts — so the failure mode is a blank white page with one console
 * error, not a useful message.
 *
 * Production builds are still protected, in two places:
 *   1. the throw below, when a production bundle has no value baked in
 *   2. scripts/check-api-base.mjs, which fails the CI build BEFORE bundling
 *
 * Belt and braces on purpose: (2) gives a readable error in the Actions log,
 * (1) catches anything that slips past it.
 *
 * Placement: frontend/src/config/apiBase.js
 */

const RAW = import.meta.env.VITE_API_URL;

/** Vite sets DEV=true for `vite dev` and false for `vite build`. */
const IS_DEV = import.meta.env.DEV;

/**
 * Strip a trailing slash, then a trailing /api, then any slash that exposed.
 * Order matters: 'https://h/api/' must survive both passes.
 */
const toOrigin = (value) =>
  String(value)
    .trim()
    .replace(/\/+$/, '')      // trailing slashes
    .replace(/\/api$/i, '')   // the /api suffix, if present
    .replace(/\/+$/, '');     // any slash the suffix was hiding

let origin;

if (RAW && String(RAW).trim()) {
  origin = toOrigin(RAW);
} else if (IS_DEV) {
  // Relative URLs; the Vite proxy in vite.config.js forwards them to Flask.
  origin = '';
} else {
  throw new Error(
    '[Enumé] VITE_API_URL is not set in a production build.\n\n' +
    'Every API call and image URL would resolve against the static site\n' +
    'origin instead of the backend, so roughly half the app would 404.\n\n' +
    'Set it in .github/workflows/azure-static-web-apps-*.yml under the\n' +
    'build step\'s env: block. Expected value:\n' +
    '  https://redsocial-api-bacxh8fgaeeadkbp.westus3-01.azurewebsites.net/api\n\n' +
    'scripts/check-api-base.mjs should have caught this before the bundle\n' +
    'was built — if you are seeing this instead, the prebuild hook is not\n' +
    'wired into package.json.',
  );
}

/**
 * Backend origin, no path. Use for:
 *   - SSE / EventSource       `${API_ORIGIN}/api/realtime/stream`
 *   - stored media paths      `${API_ORIGIN}${project.image_url}`
 *   - anything whose path already begins with /api
 * Empty string in local dev, which correctly yields a relative URL.
 */
export const API_ORIGIN = origin;

/**
 * Backend REST root, including /api. Use for hand-built fetch calls:
 *   `${API_BASE}/projects/5`
 * Prefer the shared client in src/api.js where one exists; this is for the
 * call sites that bypass it.
 */
export const API_BASE = `${origin}/api`;

/**
 * Default export is API_BASE, matching the previous version of this file so
 * existing `import API_BASE from '../config/apiBase'` sites keep working.
 * New code should use the named exports — being explicit about ORIGIN vs BASE
 * is the entire point of this module.
 */
export default API_BASE;